/* Present Judge.me's real review photo and metadata in the HNG-04 reference layout. */
(() => {
  const selector = 'body.hn-ui-reference .hn-judge-reviews .jm-review-item .jm-media-thumbnail-list__thumbnail';
  let dialog;
  let returnFocus;
  let activeItems = [];
  let activeIndex = 0;
  let touchStartX = null;

  const close = () => dialog?.close();

  const ensureDialog = () => {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'hn-ui-review-photo';
    dialog.setAttribute('aria-label', 'Preview review image');
    dialog.innerHTML = `
      <button class="hn-ui-review-photo__close" type="button" aria-label="Close review image">
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 4 16 16M16 4 4 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
      <div class="hn-ui-review-photo__body">
        <div class="hn-ui-review-photo__image-wrap">
          <button class="hn-ui-review-photo__nav hn-ui-review-photo__nav--prev" type="button" aria-label="Previous review image">‹</button>
          <img class="hn-ui-review-photo__image" alt="">
          <button class="hn-ui-review-photo__nav hn-ui-review-photo__nav--next" type="button" aria-label="Next review image">›</button>
        </div>
        <div class="hn-ui-review-photo__info">
          <div class="hn-ui-review-photo__header"><span class="hn-ui-review-photo__stars"></span><strong class="hn-ui-review-photo__author"></strong></div>
          <div class="hn-ui-review-photo__date"></div>
          <p class="hn-ui-review-photo__text"></p>
        </div>
      </div>`;
    document.body.append(dialog);
    dialog.querySelector('.hn-ui-review-photo__close').addEventListener('click', close);
    dialog.querySelector('.hn-ui-review-photo__nav--prev').addEventListener('click', () => move(-1));
    dialog.querySelector('.hn-ui-review-photo__nav--next').addEventListener('click', () => move(1));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      returnFocus?.focus();
      returnFocus = null;
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
    });
    const imageWrap = dialog.querySelector('.hn-ui-review-photo__image-wrap');
    imageWrap.addEventListener('touchstart', (event) => {
      touchStartX = event.touches[0]?.clientX ?? null;
    }, { passive: true });
    imageWrap.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const delta = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) >= 45) move(delta > 0 ? -1 : 1);
    }, { passive: true });
    return dialog;
  };

  const render = ({ imageUrl, author = 'Reviewer', rating, date = '', body = '' }) => {
    if (!imageUrl) return;
    const mediaUrl = new URL(imageUrl, location.href);
    if (mediaUrl.protocol !== 'https:') return;
    mediaUrl.searchParams.set('width', '1024');
    const modal = ensureDialog();
    const starsCount = Math.max(0, Math.min(5, Number.parseInt(rating, 10) || 0));
    const modalImage = modal.querySelector('.hn-ui-review-photo__image');
    modalImage.src = mediaUrl.href;
    modalImage.alt = `Review image by ${author}`;
    const stars = modal.querySelector('.hn-ui-review-photo__stars');
    stars.textContent = '★'.repeat(starsCount);
    stars.setAttribute('aria-label', `${starsCount} out of 5 stars`);
    modal.querySelector('.hn-ui-review-photo__author').textContent = author;
    modal.querySelector('.hn-ui-review-photo__date').textContent = date;
    const text = modal.querySelector('.hn-ui-review-photo__text');
    text.textContent = body;
    text.hidden = !body;
    const hasMultiple = activeItems.length > 1;
    modal.querySelectorAll('.hn-ui-review-photo__nav').forEach((button) => { button.hidden = !hasMultiple; });
  };

  function move(direction) {
    if (activeItems.length < 2) return;
    activeIndex = (activeIndex + direction + activeItems.length) % activeItems.length;
    render(activeItems[activeIndex]);
  }

  const show = ({ trigger, items, index = 0, imageUrl, author, rating, date, body }) => {
    if (!trigger) return;
    activeItems = Array.isArray(items) && items.length ? items : [{ imageUrl, author, rating, date, body }];
    activeIndex = Math.max(0, Math.min(activeItems.length - 1, Number(index) || 0));
    if (!activeItems[activeIndex]?.imageUrl) return;
    const modal = ensureDialog();
    render(activeItems[activeIndex]);
    returnFocus = trigger;
    modal.showModal();
    modal.querySelector('.hn-ui-review-photo__close').focus();
  };

  const open = (trigger) => {
    const triggers = [...document.querySelectorAll(selector)];
    const entries = triggers.map((candidate) => {
      const review = candidate.closest('.jm-review-item');
      const image = candidate.querySelector('img');
      return {
        trigger: candidate,
        imageUrl: image?.currentSrc || image?.src || '',
        author: review?.querySelector('.jm-reviewer-info__name')?.textContent?.trim() || 'Reviewer',
        rating: review?.querySelector('.jm-star-rating')?.getAttribute('aria-label') || '',
        date: review?.querySelector('.jm-reviewer-info__details .jm-cluster:last-child .jm-text')?.textContent?.trim() || '',
        body: review?.querySelector('.jm-review-content__body')?.textContent?.trim() || '',
      };
    }).filter((item) => item.imageUrl);
    show({
      trigger,
      items: entries,
      index: Math.max(0, entries.findIndex((entry) => entry.trigger === trigger)),
    });
  };

  document.addEventListener('hn-ui-review-photo-open', (event) => {
    if (!document.body.classList.contains('hn-ui-reference')) return;
    show(event.detail || {});
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest(selector) : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(trigger);
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const trigger = event.target instanceof Element ? event.target.closest(selector) : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(trigger);
  }, true);
})();
